import { useEffect, useState, type ChangeEvent, type ComponentProps, type FocusEvent } from "react";

import { useController, type FieldValues, type UseControllerProps } from "common/core/form";
import { StyledTextInput } from "common/core/form/text";

import Styles from "./index.module.scss";

type ObfuscatedInputProps<FormValues extends FieldValues> = {
  mask: string;
  setFocus?: (bool: boolean) => void;
  disableMask: boolean;
  fixedMask?: boolean;
  onTransform?: (value: string) => string;
  rules: UseControllerProps<FormValues>["rules"];
  control: UseControllerProps<FormValues>["control"];
  name: UseControllerProps<FormValues>["name"];
} & ComponentProps<typeof StyledTextInput>;

export function ObfuscatedInput<FormValues extends FieldValues>({
  rules,
  control,
  mask,
  setFocus,
  disableMask,
  fixedMask = true,
  onTransform,
  onBlur,
  onFocus,
  onChange,
  name,
  ...props
}: ObfuscatedInputProps<FormValues>) {
  const { field } = useController({ name, control, rules });
  function hiddenValue() {
    return fixedMask ? mask : field.value?.replace(/\d/g, mask);
  }
  const showFieldMask = !disableMask && field.value && field.value.length > 0;
  const [value, setValue] = useState(showFieldMask ? hiddenValue() : field.value);

  useEffect(() => {
    setValue(showFieldMask ? hiddenValue() : field.value);
  }, [disableMask]);

  const inputProps = {
    onFocus: (e: FocusEvent<HTMLInputElement>) => {
      onFocus?.(e);
      setFocus?.(true);
      setValue(field.value);
    },
    onBlur: (e: FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
      field.onBlur();
      setFocus?.(false);
      showFieldMask && setValue(hiddenValue());
    },
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const transformedValue = onTransform ? onTransform(rawValue) : rawValue;

      onChange?.(e);
      field.onChange(transformedValue);
      setValue(transformedValue);
    },
  };

  return (
    <StyledTextInput
      {...field}
      {...props}
      {...inputProps}
      value={value}
      className={Styles.obfuscatedInput}
    />
  );
}
